/**
 * Pagination Load More System
 * Handles infinite scroll-style loading with "Load More" button
 * Preserves scroll position when returning from PDP
 * Shows back to top button after scrolling
 */

class PaginationLoadMore {
    constructor() {
        this.loadMoreBtn = document.querySelector('[data-load-more-btn]');
        this.productGrid = document.querySelector('.new-grid.product-grid.collection-grid');
        this.paginationContainer = document.querySelector('[data-pagination]');
        this.backToTopBtn = document.querySelector('[data-back-to-top]');
        this.isLoading = false;
        this.scrollThreshold = 800; // Show back to top after 800px scroll

        if (this.loadMoreBtn && this.productGrid) {
            this.init();
        }
    }

    init() {
        // Load more button click handler
        this.loadMoreBtn.addEventListener('click', () => this.loadMore());

        // Back to top button
        if (this.backToTopBtn) {
            this.initBackToTop();
        }

        // Restore scroll position if returning from PDP
        this.restoreScrollPosition();

        // Save scroll position before navigating to PDP
        this.saveScrollPositionOnClick();
    }

    async loadMore() {
        if (this.isLoading) return;

        const nextUrl = this.loadMoreBtn.dataset.nextUrl;
        console.log('Loading more products from:', nextUrl);

        if (!nextUrl) {
            console.warn('No next URL found');
            return;
        }

        this.isLoading = true;
        this.showLoading();

        try {
            const response = await fetch(nextUrl);
            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            // Get new products
            const newProducts = doc.querySelectorAll('.new-grid.product-grid.collection-grid > *');
            console.log('Found new products:', newProducts.length);

            if (newProducts.length > 0) {
                // Append new products to grid
                newProducts.forEach(product => {
                    this.productGrid.appendChild(product.cloneNode(true));
                });
                console.log('Products appended to grid');

                // Update pagination info
                const newPagination = doc.querySelector('[data-pagination]');
                if (newPagination) {
                    const newLoadMoreBtn = newPagination.querySelector('[data-load-more-btn]');
                    const newCount = newPagination.querySelector('.pagination-load-more__current');
                    const currentCount = this.paginationContainer.querySelector('.pagination-load-more__current');

                    // Update product count
                    if (currentCount && newCount) {
                        currentCount.textContent = newCount.textContent;
                    }

                    // Update or remove load more button
                    if (newLoadMoreBtn) {
                        this.loadMoreBtn.dataset.nextUrl = newLoadMoreBtn.dataset.nextUrl;
                    } else {
                        // No more pages - show end message
                        this.showEndMessage();
                    }
                }

                // Re-initialize any product scripts (quick shop, swatches, etc.)
                if (typeof theme !== 'undefined' && theme.reinitProductGridItem) {
                    theme.reinitProductGridItem();
                }

                // Trigger custom event for other scripts to hook into
                document.dispatchEvent(new CustomEvent('collection:productsLoaded', {
                    detail: { count: newProducts.length }
                }));
            }
        } catch (error) {
            console.error('Error loading more products:', error);
            this.showError();
        } finally {
            this.isLoading = false;
            this.hideLoading();
        }
    }

    showLoading() {
        this.loadMoreBtn.classList.add('is-loading');
        this.loadMoreBtn.disabled = true;
        const btnText = this.loadMoreBtn.querySelector('.pagination-load-more__btn-text');
        const btnLoader = this.loadMoreBtn.querySelector('.pagination-load-more__btn-loader');
        if (btnText) btnText.style.display = 'none';
        if (btnLoader) btnLoader.style.display = 'inline';
    }

    hideLoading() {
        this.loadMoreBtn.classList.remove('is-loading');
        this.loadMoreBtn.disabled = false;
        const btnText = this.loadMoreBtn.querySelector('.pagination-load-more__btn-text');
        const btnLoader = this.loadMoreBtn.querySelector('.pagination-load-more__btn-loader');
        if (btnText) btnText.style.display = 'inline';
        if (btnLoader) btnLoader.style.display = 'none';
    }

    showEndMessage() {
        const actions = this.paginationContainer.querySelector('.pagination-load-more__actions');
        if (actions) {
            actions.innerHTML = `
        <div class="pagination-load-more__end">
          <p>Você visualizou todos os produtos</p>
        </div>
      `;
        }
    }

    showError() {
        alert('Erro ao carregar mais produtos. Por favor, tente novamente.');
    }

    initBackToTop() {
        // Show/hide back to top button based on scroll
        let scrollTimeout;
        window.addEventListener('scroll', () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                if (window.pageYOffset > this.scrollThreshold) {
                    this.backToTopBtn.style.display = 'flex';
                    this.backToTopBtn.classList.add('visible');
                } else {
                    this.backToTopBtn.classList.remove('visible');
                    setTimeout(() => {
                        if (!this.backToTopBtn.classList.contains('visible')) {
                            this.backToTopBtn.style.display = 'none';
                        }
                    }, 300);
                }
            }, 100);
        });

        // Scroll to top on click
        this.backToTopBtn.addEventListener('click', () => {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        });
    }

    saveScrollPositionOnClick() {
        // Save scroll position when clicking on product links
        document.addEventListener('click', (e) => {
            const productLink = e.target.closest('.product-card a, .card-product a');
            if (productLink) {
                sessionStorage.setItem('collectionScrollPosition', window.pageYOffset);
                sessionStorage.setItem('collectionUrl', window.location.href);
            }
        });
    }

    restoreScrollPosition() {
        // Only restore if coming back to the same collection page
        const savedUrl = sessionStorage.getItem('collectionUrl');
        const currentUrl = window.location.href;

        if (savedUrl && currentUrl.includes(savedUrl.split('?')[0])) {
            const savedPosition = sessionStorage.getItem('collectionScrollPosition');
            if (savedPosition) {
                // Restore scroll position after page load
                window.addEventListener('load', () => {
                    setTimeout(() => {
                        window.scrollTo(0, parseInt(savedPosition));
                    }, 100);
                });

                // Clear saved position after restoring
                sessionStorage.removeItem('collectionScrollPosition');
                sessionStorage.removeItem('collectionUrl');
            }
        }
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        new PaginationLoadMore();
    });
} else {
    new PaginationLoadMore();
}

/**
 * Sticky Filters Enhancement
 * Makes collection filters stick to the top when scrolling
 */
class StickyFilters {
    constructor() {
        this.filterBar = document.querySelector('.collection-filter');
        if (this.filterBar) {
            this.init();
        }
    }

    init() {
        let lastScroll = 0;

        window.addEventListener('scroll', () => {
            const currentScroll = window.pageYOffset;

            // Add shadow when scrolled past the filter bar
            if (currentScroll > 100) {
                this.filterBar.classList.add('is-stuck');
            } else {
                this.filterBar.classList.remove('is-stuck');
            }

            lastScroll = currentScroll;
        });
    }
}

// Initialize sticky filters
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        new StickyFilters();
    });
} else {
    new StickyFilters();
}
